import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import {
  getRouteCreateProject,
  getRouteNewVersion,
  getRouteProjectDetails,
  getRouteProjects,
  getRouteReviewRelaunch
} from "@/zapi-frontend/shared/const/router";
import { useHistoricalNavigate } from "@/zapi-frontend/shared/hooks/useHistoricalNavigate";
import { useWorkspaceLayout } from "@/zapi-frontend/shared/hooks/useWorkspaceLayout";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { useLeoTaskPolling } from "@/zapi-frontend/widgets/LeoChat";
import { selectIsLeoUpdatingAnalysis, useLeoChatStore } from "@/zapi-frontend/stores/leoChatStore";
import { LiveUpdateShimmer } from "@/zapi-frontend/shared/ui/LiveUpdateShimmer";
import { MemoIsland } from "./MemoIsland/MemoIsland";
import { useRefreshAfterUpdates } from "./ProjectSidebar/useRefreshAfterUpdates";
import { WorkspaceRoomsIsland } from "./Repository/WorkspaceRoomsIsland";
import { AnalysisIsland, AnalysisToolContent } from "./AnalysisIsland";
import { ProjectHeader } from "./ProjectHeader/ProjectHeader";
import { PageLoader } from "@/zapi-frontend/shared/ui/PageLoader";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { isChapterGenerating, useChatStore } from "@/zapi-frontend/stores/chatStore";
import { InstructionsIsland } from "./InstructionsIsland";
import { InstructionsHeader } from "./InstructionsHeader";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { toast } from "sonner";
import { useProjectSetupPolling } from "@/zapi-frontend/shared/hooks/useProjectSetupPolling";
import { useNotificationStore } from "@/zapi-frontend/features/Notifications";
import { useBackRedirectFromProjectList } from "@/zapi-frontend/shared/hooks/useBackToProjectOverride";
import { SS_BACK_TO_PROJECT_PRESENTATION } from "@/zapi-frontend/shared/session-storage/keys";
import { DashboardIsland } from "./DashboardIsland/DashboardIsland";
import { ProjectOverviewIsland } from "./ProjectOverviewIsland";
import { CaseFrameChatIsland } from "./FrameIsland/CaseFrameChatIsland";
import { VaultIsland } from "./VaultIsland/VaultIsland";
import { ExportsIsland } from "./ExportsIsland/ExportsIsland";
import { ModelLedgerIsland } from "./ModelLedgerIsland/ModelLedgerIsland";
import { RqaSimIsland } from "./RqaSimIsland/RqaSimIsland";
import { RisksIsland } from "./RisksIsland/RisksIsland";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import { ValueChainIsland } from "./ValueChainIsland/ValueChainIsland";
import { PresentationPage } from "./PresentationPage/ui/PresentationPage";
import { useProjectThemeStore } from "@/zapi-frontend/stores/projectTheme";
import { useProjectLanguageSync } from "@/zapi-frontend/shared/i18n/useProjectLanguageSync";
import { useProjectGenerationState } from "@/zapi-frontend/shared/hooks/useProjectGenerationState";
import { AnalysisInProgressScreen } from "./GenerationStates/AnalysisInProgressScreen";
import { AnalysisFailedScreen } from "./GenerationStates/AnalysisFailedScreen";
import { ChapterQueuedScreen } from "./GenerationStates/ChapterQueuedScreen";
import { ChapterGeneratingSkeleton } from "./GenerationStates/ChapterGeneratingSkeleton";
import { ProjectNav } from "./ProjectLayout/ui/ProjectNav";
import {
  ChapterSectionScreen,
  FrameSectionScreen,
  SectionStateScreen,
  ValueChainSectionScreen
} from "./ProjectLayout/ui/SectionScreens";
import { useSectionPage } from "./ProjectLayout/hooks/useSectionPage";
import { getSectionScreen } from "./ProjectLayout/lib/sectionPage";
import { WORKSPACE_ASIDE_SLOT_ID } from "./ProjectLayout/lib/layoutSlots";
import {
  AppShellAside,
  AppShellCenter,
  AppShellContent,
  AppShellMain,
  AppShellPanels
} from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ProjectLayout/ui/blocks/AppShell";
import { LatticeField } from "@libs/zapi-ui/ds/components/lattice-field";

export function ProjectDetailsPage() {
  const { id } = useParams();
  return <ProjectDetailsPageContent key={typeof id === "string" ? id : ""} />;
}

function ProjectDetailsPageContent() {
  const { t } = useTranslation("projectHome");
  const { id } = useParams();
  const projectId = useMemo(() => (typeof id === "string" ? id : ""), [id]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchSelectedProject, isFetchProjectLoading, setSelectedProject, selectedProject } = useProjectStore();
  const { initializeProjectChapters, viewMode, setViewMode, activeChapter, setActiveChapter, projectChapters } =
    useChapterStore();
  const { navigate } = useHistoricalNavigate();
  const { isSidebarDocked, isChatDocked } = useWorkspaceLayout();
  const { closeHandler, openSidebar } = useProjectSidebarStore();
  const previewIconColor = useProjectThemeStore((s) => s.previewIconColor);
  const { getLoadingState } = useChatStore();
  useProjectLanguageSync(selectedProject?.language);
  useLeoTaskPolling(projectId);
  const isSetupActive = useProjectSetupStore((s) => !!s.activeSetupTasks[projectId]);
  const isLeoUpdateRunning = useLeoChatStore((s) => !!s.activeTaskByProject[projectId]?.canEdit);
  useRefreshAfterUpdates(projectId, isSetupActive, isLeoUpdateRunning);
  const isLeoUpdatingAnalysis = useLeoChatStore(selectIsLeoUpdatingAnalysis(projectId));
  // The sidebar entry is behind the flag; a link straight to ?view=rqaSim has to be too, or the
  // page exists for an organisation the feature was never turned on for.
  const rqaSimEnabled = useFeatureFlag("rqa-sim", selectedProject?.organizationId);
  const [isProjectLoaded, setIsProjectLoaded] = useState(false);
  const [presentationChatSlot, setPresentationChatSlot] = useState<HTMLDivElement | null>(null);

  const {
    isPollingInitialized,
    chapterMemoPolling,
    chapterStatusMap,
    cancelSetup,
    isCancelling,
    canCancel,
    retrySetup,
    isRetrying
  } = useProjectSetupPolling(
    projectId,
    activeChapter?.id ?? null,
    isProjectLoaded,
    useCallback(async () => {
      // The generation may have created chapter rows that did not exist on page load
      // (template-less relaunch starts from zero), so the chapter store needs the fresh payload too.
      const response = await fetchSelectedProject(projectId, true);
      if (response) initializeProjectChapters(response);
      setIsProjectLoaded(true);
    }, [projectId, fetchSelectedProject, initializeProjectChapters])
  );

  const chapterMemoStatus = chapterMemoPolling.chapters.find((i) => i.id === activeChapter?.id);
  const isActiveChapterHasActiveMemo = chapterMemoStatus?.hasActiveMemo ?? !!activeChapter?.activeMemo;

  const handleCancelSetup = useCallback(async () => {
    const result = await cancelSetup();
    if (!result) return;

    useNotificationStore.getState().removeProjectNotifications(Number(projectId));

    if (result.isRelaunchBrief) {
      navigate(getRouteReviewRelaunch(projectId));
    } else if (selectedProject?.versionGroupId) {
      navigate(getRouteNewVersion(projectId));
    } else {
      navigate(`${getRouteCreateProject()}?caseBriefId=${result.caseBriefId}`);
    }
  }, [cancelSetup, navigate, projectId, selectedProject?.versionGroupId]);

  const loadProject = useCallback(async () => {
    const [response, error] = await tryCatch(fetchSelectedProject(projectId));

    if (error) {
      toast.error("Failed to load project");
      navigate(getRouteProjects());
      return;
    }

    if (response) {
      initializeProjectChapters(response);
      setIsProjectLoaded(true);
    }
  }, [projectId, fetchSelectedProject, initializeProjectChapters, navigate]);

  useEffect(() => {
    if (!projectId) return;
    setIsProjectLoaded(false);
    loadProject();
  }, [projectId, loadProject]);

  useEffect(() => {
    const hasChapterId = searchParams.has("chapterId");
    const viewParam = searchParams.get("view");

    if (viewParam === "dashboard" && viewMode !== "dashboard") {
      setViewMode("dashboard");
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete("view");
          return params;
        },
        { replace: true }
      );
    } else if (viewParam === "instructions") {
      setViewMode("instructions");
    } else if (
      viewParam === "projectFrame" ||
      viewParam === "projectConfig" ||
      viewParam === "vault" ||
      viewParam === "exports" ||
      viewParam === "valueChain" ||
      viewParam === "modelLedger" ||
      viewParam === "rqaSim" ||
      viewParam === "risks"
    ) {
      if (viewMode !== viewParam) setViewMode(viewParam);
    } else if (hasChapterId) {
      const chapterIdParam = searchParams.get("chapterId");
      if (
        chapterIdParam &&
        chapterIdParam !== activeChapter?.id?.toString() &&
        projectChapters.some((c) => c.id === Number(chapterIdParam))
      ) {
        setActiveChapter(chapterIdParam);
      }
      if (viewMode !== "chapter") {
        setViewMode("chapter");
      }
    } else if (projectId) {
      // Home is the only fallback view now, so it can be picked before the project
      // loads — waiting left the header on "Loading project..." with chapter tools.
      if (viewMode === "instructions" || (!viewParam && viewMode !== "dashboard")) {
        setViewMode("projectOverview");
      }
    }
  }, [
    searchParams,
    viewMode,
    setViewMode,
    setActiveChapter,
    activeChapter?.id,
    setSearchParams,
    projectId,
    isProjectLoaded,
    projectChapters,
    isPollingInitialized,
    canCancel
  ]);

  useEffect(() => {
    return () => {
      setViewMode(undefined);
      setSelectedProject(null);
      closeHandler();
      useProjectThemeStore.getState().setPreviewIconColor(null);
    };
  }, [setViewMode, setSelectedProject, closeHandler]);

  useEffect(() => {
    if (isFetchProjectLoading || !isActiveChapterHasActiveMemo) closeHandler();
  }, [isFetchProjectLoading, isActiveChapterHasActiveMemo, closeHandler]);
  // Until check-active (or a stored task) answers we do not know whether the project is
  // generating; rendering real content before that flashes it for a few seconds.
  const isGenerationResolved = isPollingInitialized || canCancel;
  const isViewModeResolved = searchParams.has("chapterId") || isGenerationResolved;

  const generationState = useProjectGenerationState(projectId);
  const handleRetrySetup = useCallback(async () => {
    if (generationState.debugState) {
      toast.info("debugGenState is on — retry is stubbed");
      return;
    }
    await retrySetup();
  }, [generationState.debugState, retrySetup]);
  const isDisabledDueToProjectSetup =
    (generationState.isSetupActive && !isActiveChapterHasActiveMemo) || !!generationState.debugState;
  const queuedChapterPills = activeChapter ? generationState.getQueuedPills(activeChapter) : [];
  const generationProgress = generationState.isSetupActive
    ? {
        projectId,
        onCancel: generationState.debugState
          ? () => toast.info("debugGenState is on — cancel is stubbed")
          : canCancel
          ? handleCancelSetup
          : undefined,
        isCancelling,
        isSettingUp: generationState.isSettingUp,
        setup: generationState.setup
      }
    : undefined;

  const activeChapterId = activeChapter?.id?.toString() ?? "";
  const isChatLoading = isChapterGenerating(getLoadingState(projectId, activeChapterId));

  const isPresentationTab = searchParams.get("tab") === "presentation";
  const sectionPage = useSectionPage();
  const isChromelessView =
    viewMode === "projectOverview" ||
    viewMode === "projectFrame" ||
    viewMode === "projectConfig" ||
    viewMode === "vault" ||
    viewMode === "exports" ||
    viewMode === "modelLedger" ||
    viewMode === "rqaSim" ||
    viewMode === "risks";
  const isFrameOrArtifactsView =
    viewMode === "projectFrame" ||
    viewMode === "projectConfig" ||
    viewMode === "vault" ||
    viewMode === "valueChain" ||
    viewMode === "exports" ||
    viewMode === "modelLedger" ||
    viewMode === "rqaSim" ||
    viewMode === "risks";
  const showPageLoader = isFrameOrArtifactsView
    ? isFetchProjectLoading
    : (isFetchProjectLoading || !isViewModeResolved || (viewMode === "instructions" && isDisabledDueToProjectSetup)) &&
      viewMode !== "dashboard" &&
      viewMode !== "projectOverview";
  // Generation screens keep the page chrome (title + divider) and a white content panel,
  // per design — unlike the finished Home, which is chromeless at `hd`. The Home skeleton
  // is the exception: it reuses the overview's cards-on-gray layout.
  const isHomeGenerationView = viewMode === "projectOverview" && generationState.isSetupActive;
  const showHomeGenerationChrome = isHomeGenerationView;
  const showHomeGenerationPanel = isHomeGenerationView && !generationState.isHomeGenerating;
  const showSetupFailedScreen =
    !showPageLoader && !!generationState.setupFailure && viewMode !== "projectFrame" && viewMode !== "projectConfig";
  const showSetupScreen =
    !showSetupFailedScreen &&
    !showPageLoader &&
    generationState.isSetupActive &&
    generationState.isSettingUp &&
    !generationState.isGenerationDataPending &&
    viewMode !== "projectFrame" &&
    viewMode !== "projectConfig";
  const showChapterGenerationState =
    !showPageLoader && !showSetupScreen && viewMode === "chapter" && isDisabledDueToProjectSetup;
  const showChapterChatSkeleton =
    !showPageLoader &&
    !showSetupScreen &&
    !showChapterGenerationState &&
    viewMode === "chapter" &&
    isChatLoading &&
    !isActiveChapterHasActiveMemo;

  const isContentShown =
    !showPageLoader &&
    !showSetupFailedScreen &&
    !showSetupScreen &&
    !showChapterGenerationState &&
    !showChapterChatSkeleton;
  // A chapter still fetching its body shows a loader, not content. A templated project's Home is
  // rebuilt only after the turn ends, out of the page's sight, so it would stop shimmering too soon.
  const isAnalysisView =
    (viewMode === "chapter" && !!activeChapter?.activeMemo?.mdContent) ||
    (viewMode === "projectOverview" && selectedProject?.projectTemplateSlug === "") ||
    viewMode === "modelLedger" ||
    ((viewMode === "rqaSim" || viewMode === "risks") && rqaSimEnabled);
  const showLiveUpdateShimmer = isLeoUpdatingAnalysis && isContentShown && isAnalysisView;
  const sectionScreen = getSectionScreen({
    hasSectionPage: !!sectionPage,
    isContentShown,
    viewMode,
    isProjectLoaded,
    isTemplateLess: selectedProject?.projectTemplateSlug === ""
  });
  const toolsInWidget = sectionScreen === "chapter";
  const isSectionPageLoading =
    showPageLoader &&
    (viewMode === "chapter" ||
      viewMode === "projectFrame" ||
      viewMode === "projectConfig" ||
      viewMode === "valueChain");
  const hasStateScreen =
    showSetupFailedScreen || showSetupScreen || showChapterGenerationState || showChapterChatSkeleton;
  const isSectionStateView = hasStateScreen && !!sectionPage && (viewMode === "chapter" || viewMode === "valueChain");
  const isChromeless = isChromelessView || !!sectionScreen || isSectionPageLoading || isSectionStateView;
  const areChapterToolsDisabled =
    !isActiveChapterHasActiveMemo || isFetchProjectLoading || generationState.isSetupActive;
  // These pages carry their own title bar, which stays out of the shimmer like the chapter header.
  const hasIslandTitleBar = viewMode === "modelLedger" || viewMode === "rqaSim" || viewMode === "risks";

  useBackRedirectFromProjectList({
    projectId,
    isActive: isPresentationTab,
    storageKey: SS_BACK_TO_PROJECT_PRESENTATION,
    returnUrl: `${getRouteProjectDetails(projectId)}?tab=document`
  });

  const chatAsideClassName = isChatDocked
    ? "sticky top-(--app-header-height) z-31 flex h-[calc(100svh-var(--app-header-height))] shrink-0 py-4"
    : "contents";

  if (isPresentationTab)
    return (
      <ProjectShell projectId={projectId} aside={<div ref={setPresentationChatSlot} className={chatAsideClassName} />}>
        <PresentationPage chatSlot={presentationChatSlot} />
      </ProjectShell>
    );

  const stateScreenClassName =
    "w-full min-w-0 h-full min-h-[calc(100svh-var(--app-header-height)-2rem)] rounded-xl bg-white overflow-y-auto shadow-[0px_51px_14px_0px_rgba(24,24,27,0),0px_33px_13px_0px_rgba(24,24,27,0),0px_18px_11px_0px_rgba(24,24,27,0.01),0px_8px_8px_0px_rgba(24,24,27,0.01),0px_2px_5px_0px_rgba(24,24,27,0.02)]";

  const stateContent = showSetupFailedScreen ? (
    <AnalysisFailedScreen
      error={generationState.setupFailure ?? ""}
      onRetry={handleRetrySetup}
      isRetrying={isRetrying}
    />
  ) : showSetupScreen ? (
    <AnalysisInProgressScreen
      setup={generationState.setup}
      accent={previewIconColor ?? selectedProject?.iconColor ?? undefined}
      onCancel={
        generationState.debugState
          ? () => toast.info("debugGenState is on — cancel is stubbed")
          : canCancel
          ? handleCancelSetup
          : undefined
      }
      isCancelling={isCancelling}
    />
  ) : showChapterGenerationState ? (
    generationState.isGenerationDataPending ? (
      <PageLoader />
    ) : activeChapter && generationState.getChapterState(activeChapter) === "generating" ? (
      <ChapterGeneratingSkeleton />
    ) : (
      <ChapterQueuedScreen
        title={activeChapter?.projectTemplateChapter.name ?? t("generation.queuedFallbackTitle")}
        pills={queuedChapterPills}
      />
    )
  ) : showChapterChatSkeleton ? (
    <ChapterGeneratingSkeleton />
  ) : null;

  return (
    <ProjectShell
      projectId={projectId}
      aside={
        <>
          <div id={WORKSPACE_ASIDE_SLOT_ID} className="contents" />
          <div className={chatAsideClassName}>
            {viewMode === "chapter" && <WorkspaceRoomsIsland generationProgress={generationProgress} />}
            {viewMode === "projectFrame" &&
              !generationState.isSetupActive &&
              (isProjectLoaded ? (
                <CaseFrameChatIsland />
              ) : (
                <WorkspaceRoomsIsland askOnly generationProgress={generationProgress} />
              ))}
            {((viewMode === "projectFrame" && generationState.isSetupActive) ||
              viewMode === "projectOverview" ||
              viewMode === "projectConfig" ||
              viewMode === "vault" ||
              viewMode === "valueChain" ||
              viewMode === "exports" ||
              viewMode === "modelLedger" ||
              viewMode === "rqaSim" ||
              viewMode === "risks") && (
              <WorkspaceRoomsIsland
                chapter={
                  projectChapters.find((c) => c.projectTemplateChapter.slug === "executive-summary-10-pager") ??
                  projectChapters.find(
                    (c) =>
                      c.projectTemplateChapter.isExecutiveSummary ||
                      c.projectTemplateChapter.slug.startsWith("executive-summary")
                  ) ??
                  projectChapters[0]
                }
                askOnly
                generationProgress={generationProgress}
              />
            )}
          </div>
        </>
      }
    >
      <div
        className={cn(
          "flex flex-col flex-1 min-w-0",
          isChromeless && !showHomeGenerationPanel
            ? "bg-transparent"
            : "overflow-hidden rounded-xl border border-innoveraZinc-200 bg-white"
        )}
      >
        {viewMode === "instructions" && <InstructionsHeader />}
        {viewMode !== "instructions" && (
          <div className={cn(isChromeless && !showHomeGenerationChrome && isSidebarDocked && "hidden")}>
            <ProjectHeader
              chapterMemoPolling={chapterMemoPolling}
              hideTitle={isChromeless && !showHomeGenerationChrome}
              hideChapterTools={!!sectionScreen || isSectionPageLoading || isSectionStateView}
            />
          </div>
        )}
        <div
          className={cn(
            "relative grid w-full grid-cols-[minmax(0,1fr)_0px] grid-rows-[minmax(0,1fr)] flex-1 min-h-0 transition-all duration-300",
            !!openSidebar && !toolsInWidget && "grid-cols-[0px_minmax(0,1fr)] tablet:grid-cols-[minmax(0,1fr)_312px]"
          )}
        >
          {showPageLoader ? (
            <PageLoader className={stateScreenClassName} />
          ) : isSectionStateView && sectionPage ? (
            <SectionStateScreen page={sectionPage}>{stateContent}</SectionStateScreen>
          ) : stateContent ? (
            <div className={stateScreenClassName}>{stateContent}</div>
          ) : (
            <>
              {viewMode === "dashboard" && (
                <DashboardIsland
                  chapterStatusMap={chapterStatusMap}
                  chapterMemoPolling={chapterMemoPolling}
                  onCancelSetup={canCancel ? handleCancelSetup : undefined}
                  isCancelling={isCancelling}
                />
              )}
              {viewMode === "projectOverview" && <ProjectOverviewIsland isGenerationResolved={isGenerationResolved} />}
              {sectionScreen === "frame" && sectionPage && <FrameSectionScreen page={sectionPage} />}
              {viewMode === "vault" && isProjectLoaded && <VaultIsland />}
              {sectionScreen === "valueChain" && sectionPage && <ValueChainSectionScreen page={sectionPage} />}
              {!sectionScreen &&
                viewMode === "valueChain" &&
                isProjectLoaded &&
                selectedProject?.projectTemplateSlug !== "" && <ValueChainIsland />}
              {viewMode === "exports" && isProjectLoaded && <ExportsIsland />}
              {viewMode === "modelLedger" && isProjectLoaded && <ModelLedgerIsland />}
              {viewMode === "rqaSim" && isProjectLoaded && rqaSimEnabled && <RqaSimIsland />}
              {viewMode === "risks" && isProjectLoaded && rqaSimEnabled && <RisksIsland />}
              {sectionScreen === "chapter" && sectionPage && (
                <ChapterSectionScreen
                  page={sectionPage}
                  toolsDisabled={areChapterToolsDisabled}
                  toolPanel={
                    <AnalysisToolContent projectId={projectId} isChapterContentReady={isActiveChapterHasActiveMemo} />
                  }
                />
              )}
              {!sectionScreen && (viewMode === "chapter" || viewMode === "instructions") && (
                <div className="w-full min-w-0 flex">
                  {viewMode === "instructions" && <InstructionsIsland />}
                  {viewMode !== "instructions" && (
                    <div
                      className={cn(
                        "w-full min-w-0",
                        activeChapter?.activeMemo?.contentFormat !== "blocks" && "laptop:px-7"
                      )}
                    >
                      <MemoIsland />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {showLiveUpdateShimmer && (
            <LiveUpdateShimmer
              className={cn(
                "absolute inset-x-0 bottom-0 z-20 col-start-1 col-end-2 row-start-1 row-end-2",
                hasIslandTitleBar ? "top-14" : "top-0"
              )}
            />
          )}
          {!toolsInWidget && (
            <AnalysisIsland projectId={projectId} isChapterContentReady={isActiveChapterHasActiveMemo} />
          )}
        </div>
      </div>
    </ProjectShell>
  );
}

function ProjectShell({ projectId, aside, children }: { projectId: string; aside: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-1">
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <LatticeField />
      </div>
      <ProjectNav projectId={projectId} />
      <AppShellPanels>
        <AppShellCenter>
          <AppShellMain className="mx-0 max-w-[960px] min-w-0 px-0 pt-4 pb-0 sm:px-0">
            <AppShellContent size="wide" className="data-[size=wide]:max-w-none">
              {children}
            </AppShellContent>
          </AppShellMain>
        </AppShellCenter>
        <AppShellAside>{aside}</AppShellAside>
      </AppShellPanels>
    </div>
  );
}
